import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Star as StarIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

interface SourceFile {
  /** Display name shown in the sidebar. */
  name: string;
  /** Raw file content. */
  content: string;
  /** True when this is the primary/main schema file. */
  isMain: boolean;
}

interface SourceFilesPreviewProps {
  /**
   * `definition.source_files` from the schema — keys are filenames
   * (or `"__main__"` for the primary file), values are raw content.
   * Pass `undefined` / `null` when the schema predates this feature.
   */
  sourceFiles: Record<string, string> | null | undefined;
}

/**
 * Read-only preview panel for the XML/XSD source files that were
 * uploaded with a template schema. Mirrors the two-column layout used
 * by the PR's FilePreviewListAndEditor: file list on the left, raw
 * content in a monospace viewer on the right.
 */
const SourceFilesPreview: React.FC<SourceFilesPreviewProps> = ({ sourceFiles }) => {
  const [selectedName, setSelectedName] = useState<string | null>(null);

  // Build a stable list from the sourceFiles map.
  const files: SourceFile[] = React.useMemo(() => {
    if (!sourceFiles || Object.keys(sourceFiles).length === 0) return [];
    const result: SourceFile[] = [];
    // __main__ always comes first.
    if (sourceFiles['__main__'] !== undefined) {
      result.push({ name: 'main_schema', content: sourceFiles['__main__'], isMain: true });
    }
    Object.entries(sourceFiles).forEach(([key, content]) => {
      if (key !== '__main__') {
        result.push({ name: key, content, isMain: false });
      }
    });
    return result;
  }, [sourceFiles]);

  // Auto-select the first file when the list loads.
  useEffect(() => {
    if (files.length > 0 && (selectedName === null || !files.find((f) => f.name === selectedName))) {
      setSelectedName(files[0].name);
    }
    if (files.length === 0) setSelectedName(null);
  }, [files, selectedName]);

  // ── No source files stored (schema predates this feature) ──────────
  if (!sourceFiles || Object.keys(sourceFiles).length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Source file content is not available for this schema — it was uploaded before the
          preview feature was added. Re-upload the schema to enable the Source Files tab.
        </Alert>
      </Box>
    );
  }

  const selectedFile = files.find((f) => f.name === selectedName) ?? null;

  // Guess the file extension for a subtle type chip.
  const getExtChip = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'xsd') return <Chip label="XSD" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 16 }} />;
    if (ext === 'xml') return <Chip label="XML" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 16 }} />;
    if (ext === 'json') return <Chip label="JSON" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.65rem', height: 16 }} />;
    if (ext === 'sql') return <Chip label="SQL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 16 }} />;
    return null;
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
      {/* Header hint */}
      <Alert severity="info" icon={<DescriptionIcon fontSize="small" />} sx={{ py: 0.5, flexShrink: 0 }}>
        Read-only view of the originally uploaded source files. Edit the schema structure using the
        <strong> Field Editor</strong> tab.
      </Alert>

      {/* Two-column body */}
      <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>

        {/* ── Left: file sidebar ──────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{
            width: 230,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Sidebar header */}
          <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.5}>
              FILES
            </Typography>
          </Box>

          <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            {files.map((file) => (
              <React.Fragment key={file.name}>
                <ListItemButton
                  selected={selectedName === file.name}
                  onClick={() => setSelectedName(file.name)}
                  sx={{ py: 0.75, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                        {/* Main-file star badge */}
                        {file.isMain && (
                          <StarIcon
                            sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }}
                            titleAccess="Main schema file"
                          />
                        )}
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ flex: 1, fontSize: '0.8rem' }}
                          title={file.name}
                        >
                          {file.name}
                        </Typography>
                        {getExtChip(file.name)}
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.disabled" noWrap>
                        {(file.content.length / 1024).toFixed(1)} KB
                      </Typography>
                    }
                  />
                </ListItemButton>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* ── Right: content viewer ───────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedFile ? (
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* File header bar */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {selectedFile.isMain && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 12 }} />}
                    label="Main"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                  {(selectedFile.content.length / 1024).toFixed(1)} KB
                </Typography>
              </Box>

              {/* Content area */}
              <Box
                component="pre"
                sx={{
                  flex: 1,
                  m: 0,
                  p: 2,
                  overflow: 'auto',
                  fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
                  fontSize: '0.8rem',
                  lineHeight: 1.6,
                  color: 'text.primary',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
                  whiteSpace: 'pre',
                  wordBreak: 'normal',
                  /* Subtle XML coloring via CSS — no heavy library needed */
                  '& .xml-tag': { color: '#0071a8' },
                }}
              >
                <XmlColoredContent content={selectedFile.content} />
              </Box>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                Select a file from the list to preview its content.
              </Typography>
            </Paper>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

// ─── Lightweight XML syntax highlighter ──────────────────────────────────────
// Avoids adding a heavy library. Splits the content into segments and
// colours XML tags, attributes, and string values with inline spans.

interface Segment {
  text: string;
  color?: string;
}

function tokenizeXml(content: string): Segment[] {
  // Simple regex-based tokenizer for XML-like content.
  const segments: Segment[] = [];
  // Matches: opening/closing tags, attribute names, attribute values, content
  const re =
    /(<\/?[\w:.-]+)([^>]*?)(\/?>)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|([^<"']+|<)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(content)) !== null) {
    if (m[1] !== undefined) {
      // Tag name
      segments.push({ text: m[1], color: '#0069c0' });
      // Attributes section — highlight attr names and values
      if (m[2]) {
        const attrRe = /([\w:.-]+)(=)("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g;
        let attrM: RegExpExecArray | null;
        let attrLast = 0;
        while ((attrM = attrRe.exec(m[2])) !== null) {
          if (attrM.index > attrLast) {
            segments.push({ text: m[2].slice(attrLast, attrM.index) });
          }
          segments.push({ text: attrM[1], color: '#e05000' }); // attr name
          segments.push({ text: '=' });
          segments.push({ text: attrM[3], color: '#089f5b' }); // attr value
          attrLast = attrM.index + attrM[0].length;
        }
        if (attrLast < m[2].length) {
          segments.push({ text: m[2].slice(attrLast) });
        }
      }
      // Closing >
      segments.push({ text: m[3], color: '#0069c0' });
    } else if (m[4] !== undefined) {
      // Standalone quoted string
      segments.push({ text: m[4], color: '#089f5b' });
    } else if (m[5] !== undefined) {
      segments.push({ text: m[5] });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < content.length) {
    segments.push({ text: content.slice(lastIndex) });
  }
  return segments;
}

const XmlColoredContent: React.FC<{ content: string }> = React.memo(({ content }) => {
  // Only tokenize for XML-like content; fall back to plain text for JSON.
  const isXmlLike = content.trimStart().startsWith('<');
  if (!isXmlLike) {
    return <>{content}</>;
  }
  const segments = tokenizeXml(content);
  return (
    <>
      {segments.map((seg, i) =>
        seg.color ? (
          <span key={i} style={{ color: seg.color }}>
            {seg.text}
          </span>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </>
  );
});

export default SourceFilesPreview;
